import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Tema claro y oscuro (ADR 0003). El SPEC 16 original tenia un solo tema; la
 * plantilla trae los dos y el conmutador.
 *
 * Se guarda la eleccion, y mientras no haya eleccion manda el sistema: quien
 * tiene el telefono en oscuro no quiere que una app le tire un fondo blanco a
 * las once de la noche.
 */

export type Theme = "light" | "dark";

const KEY = "theme";

/** El color de la barra del navegador, para que no desentone con la app. */
const BROWSER_BAR: Record<Theme, string> = {
  light: "#f3f5f9",
  dark: "#0f1420",
};

export function readTheme(): Theme {
  const saved = read();

  if (saved !== null) return saved;

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", BROWSER_BAR[theme]);
}

/**
 * El tema contrario. Separada y PURA a proposito.
 *
 * Cuando esta linea vivia dentro del actualizador de `setTheme` y ademas
 * GUARDABA en localStorage, el conmutador no hacia nada en desarrollo: React
 * comprueba que los actualizadores sean puros invocandolos dos veces, la
 * segunda con el resultado de la primera, asi que claro -> oscuro -> claro
 * (BUGLOG 2026-09-11). Un actualizador que solo calcula sobrevive a esa doble
 * llamada; uno que ademas escribe, no.
 */
export function nextTheme(current: Theme): Theme {
  return current === "dark" ? "light" : "dark";
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(readTheme);

  // Solo se guarda lo que el usuario ELIGIO. Guardar tambien el valor de
  // partida congelaria la preferencia del sistema como si fuera una eleccion, y
  // quien pone el telefono en oscuro por la noche dejaria de ver el cambio.
  const chosen = useRef(false);

  useEffect(() => {
    applyTheme(theme);

    if (chosen.current) {
      write(theme);
    }
  }, [theme]);

  const toggle = useCallback(() => {
    chosen.current = true;
    setTheme(nextTheme);
  }, []);

  return { theme, toggle };
}

/**
 * localStorage puede lanzar en modo privado o con las cookies de sitio
 * bloqueadas. Una preferencia de tema no vale una pantalla en blanco.
 */
function read(): Theme | null {
  try {
    const saved = window.localStorage.getItem(KEY);

    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null;
  }
}

function write(theme: Theme): void {
  try {
    window.localStorage.setItem(KEY, theme);
  } catch {
    // Sin sitio donde guardar: el tema dura lo que dure la pestana.
  }
}
